import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { colors, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const token = () => localStorage.getItem('access_token');
const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' });

const KAT_RENK = { YILDIZ: '#28a745', BULMACA: '#007bff', SAVASCI: '#fd7e14', KOPEK: '#dc3545' };
const KAT_EMOJI = { YILDIZ: '⭐', BULMACA: '🧩', SAVASCI: '🐎', KOPEK: '🐕' };
const KAT_LABEL = { YILDIZ: 'Yıldız', BULMACA: 'Bulmaca', SAVASCI: 'Savaşçı', KOPEK: 'Köpek' };
const TREND_ICON = { YUKSELIYOR: '📈', DUSUYOR: '📉', STABIL: '➡️' };

const Badge = ({ kategori }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
    borderRadius: 12, fontSize: 12, fontWeight: 'bold',
    background: KAT_RENK[kategori] + '18', color: KAT_RENK[kategori]
  }}>
    {KAT_EMOJI[kategori]} {KAT_LABEL[kategori]}
  </span>
);

const FcBadge = ({ value }) => {
  const renk = value < 30 ? '#28a745' : value > 35 ? '#dc3545' : '#fd7e14';
  return <span style={{ color: renk, fontWeight: 'bold' }}>%{value?.toFixed(1)}</span>;
};

export default function AnalizSonuclariPage() {
  const [analizler, setAnalizler] = useState([]);
  const [secili, setSecili] = useState(null);
  const [detay, setDetay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useUrlState('filtre', 'TUMU');
  const [siralama, setSiralama] = useState('toplam_katki');

  const loadAnalizler = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/menu-muhendisligi/analizler`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setAnalizler(data);
        if (data.length > 0) setSecili(data[0].analiz_id);
      }
    } catch (err) { logError(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAnalizler(); }, [loadAnalizler]);

  useEffect(() => {
    if (!secili) return;
    const load = async () => {
      const res = await fetch(`${API}/api/menu-muhendisligi/analiz/${secili}`, { headers: headers() });
      if (res.ok) setDetay(await res.json());
    };
    load();
  }, [secili]);

  const filtrelenmis = (detay?.detaylar || [])
    .filter(d => filtre === 'TUMU' || d.kategori === filtre)
    .sort((a, b) => {
      if (siralama === 'toplam_katki') return (b.toplam_katki || 0) - (a.toplam_katki || 0);
      if (siralama === 'food_cost') return (a.food_cost || 0) - (b.food_cost || 0);
      if (siralama === 'satis_adedi') return (b.satis_adedi || 0) - (a.satis_adedi || 0);
      if (siralama === 'katki_payi') return (b.katki_payi || 0) - (a.katki_payi || 0);
      return 0;
    });

  if (loading && analizler.length === 0) return <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, color: colors.textPrimary }}>🔬 Analiz Sonuçları</h1>
      <p style={{ margin: '0 0 20px', color: colors.textTertiary, fontSize: 13 }}>Dönem bazlı menü performans analizi</p>

      {/* Dönem Seçici */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={secili || ''} onChange={e => setSecili(Number(e.target.value))}
          style={{ padding: '8px 12px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 14 }}>
          {analizler.map(a => (
            <option key={a.analiz_id} value={a.analiz_id}>{a.donem} ({a.toplam_urun} ürün)</option>
          ))}
        </select>

        {/* Kategori filtre */}
        {['TUMU', 'YILDIZ', 'BULMACA', 'SAVASCI', 'KOPEK'].map(k => (
          <button key={k} onClick={() => setFiltre(k)} style={{
            padding: '6px 14px', borderRadius: 16, border: '1px solid',
            borderColor: filtre === k ? (k === 'TUMU' ? '#2c3e50' : KAT_RENK[k]) : '#ddd',
            background: filtre === k ? (k === 'TUMU' ? '#2c3e50' : KAT_RENK[k]) : '#fff',
            color: filtre === k ? '#fff' : '#666',
            cursor: 'pointer', fontSize: 12, fontWeight: 'bold'
          }}>
            {k === 'TUMU' ? 'Tümü' : `${KAT_EMOJI[k]} ${KAT_LABEL[k]}`}
          </button>
        ))}

        <select value={siralama} onChange={e => setSiralama(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, marginLeft: 'auto' }}>
          <option value="toplam_katki">Toplam Katkı ↓</option>
          <option value="katki_payi">Katkı Payı ↓</option>
          <option value="satis_adedi">Satış ↓</option>
          <option value="food_cost">Food Cost ↑</option>
        </select>
      </div>

      {/* Özet Kartları */}
      {detay?.ozet && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { l: 'Toplam Satış', v: `${detay.ozet.toplam_satis_adedi} adet`, c: '#2c3e50' },
            { l: 'Toplam Ciro', v: `₺${detay.ozet.toplam_ciro?.toLocaleString('tr-TR')}`, c: '#007bff' },
            { l: 'Toplam Katkı', v: `₺${detay.ozet.toplam_katki?.toLocaleString('tr-TR')}`, c: '#28a745' },
            { l: 'Ort. Food Cost', v: `%${detay.ozet.ortalama_food_cost?.toFixed(1)}`, c: detay.ozet.ortalama_food_cost > 35 ? '#dc3545' : '#28a745' },
          ].map((k, i) => (
            <div key={i} style={{
              flex: 1, minWidth: 160, background: colors.surface, borderRadius: 8, padding: '12px 16px',
              boxShadow: shadows.sm, borderLeft: `3px solid ${k.c}`
            }}>
              <div style={{ fontSize: 11, color: colors.textTertiary }}>{k.l}</div>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: colors.textPrimary }}>{k.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Detay Tablosu */}
      <div style={{ background: colors.surface, borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: colors.backgroundAlt }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Ürün</th>
              <th style={{ padding: '10px 8px', textAlign: 'right' }}>Satış Fiyatı</th>
              <th style={{ padding: '10px 8px', textAlign: 'right' }}>Maliyet</th>
              <th style={{ padding: '10px 8px', textAlign: 'right' }}>Katkı Payı</th>
              <th style={{ padding: '10px 8px', textAlign: 'right' }}>Food Cost</th>
              <th style={{ padding: '10px 8px', textAlign: 'right' }}>Satış</th>
              <th style={{ padding: '10px 8px', textAlign: 'right' }}>Top. Katkı</th>
              <th style={{ padding: '10px 8px', textAlign: 'center' }}>Kategori</th>
              <th style={{ padding: '10px 8px', textAlign: 'center' }}>Trend</th>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Öneri</th>
            </tr>
          </thead>
          <tbody>
            {filtrelenmis.map((d, i) => (
              <tr key={d.id || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>{d.urun}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}>₺{d.satis_fiyati?.toFixed(2)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}>₺{d.porsiyon_maliyeti?.toFixed(2)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 'bold' }}>₺{d.katki_payi?.toFixed(2)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}><FcBadge value={d.food_cost} /></td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}>{d.satis_adedi}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right' }}>₺{d.toplam_katki?.toFixed(0)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'center' }}><Badge kategori={d.kategori} /></td>
                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                  {d.trend ? TREND_ICON[d.trend] : '-'}
                  {d.onceki_kategori && d.onceki_kategori !== d.kategori && (
                    <span style={{ fontSize: 10, color: colors.textTertiary, display: 'block' }}>
                      {KAT_LABEL[d.onceki_kategori]} → {KAT_LABEL[d.kategori]}
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 12px', fontSize: 12, color: '#555', maxWidth: 250 }}>
                  {d.oneri ? (d.oneri.length > 80 ? d.oneri.substring(0, 80) + '...' : d.oneri) : '-'}
                </td>
              </tr>
            ))}
            {filtrelenmis.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 30, textAlign: 'center', color: colors.textTertiary }}>
                {analizler.length === 0 ? 'Henüz analiz yapılmamış. Dashboard\'dan analiz çalıştırın.' : 'Bu filtreye uygun ürün yok.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
